import { Point } from '@ardoq/math';
import { Rect } from '../Rect';
import { Rectangle } from '../types';

/** Returns the intersection (overlap) of two rectangles. */
export const intersect = (rectA: Rect, rectB: Rect) => {
  if (!intersects(rectA, rectB)) {
    return Rect.EMPTY;
  }
  const left = Math.max(rectA.left, rectB.left);
  const top = Math.max(rectA.top, rectB.top);
  return new Rect(
    left,
    top,
    Math.min(rectA.right, rectB.right) - left,
    Math.min(rectA.bottom, rectB.bottom) - top
  );
};
/** Returns true if the given rectangles are intersecting (overlapping). */
export const intersects = (rectA: Rect, rectB: Rect): boolean =>
  rectA.left < rectB.right &&
  rectB.left < rectA.right &&
  rectA.top < rectB.bottom &&
  rectB.top < rectA.bottom;

export const someIntersections = (candidateRect: Rect, arrangedRects: Rect[]) =>
  arrangedRects.some(arrangedRect => intersects(candidateRect, arrangedRect));

export const getRectCenter = (rect: Rect) =>
  getRectangleCenter([rect.left, rect.top, rect.width, rect.height]);
export const getRectangleCenter = ([x, y, width, height]: Rectangle): Point => [
  x + width / 2,
  y + height / 2,
];

export const offset = (rect: Rect, [offsetX, offsetY]: Point) =>
  new Rect(rect.left + offsetX, rect.top + offsetY, rect.width, rect.height);

export const inside = (rect: Rect, bounds: Rect): boolean =>
  rect.left >= bounds.left &&
  rect.top >= bounds.top &&
  rect.right <= bounds.right &&
  rect.bottom <= bounds.bottom;

export const shiftInto = (rect: Rect, bounds: Rect): Rect => {
  if (inside(rect, bounds)) {
    return rect;
  }
  const leftShift = Math.max(0, rect.right - bounds.right);
  const rightShift = leftShift > 0 ? 0 : Math.max(0, bounds.left - rect.left);
  const downShift = Math.max(0, bounds.top - rect.top);
  const upShift = downShift > 0 ? 0 : Math.max(0, rect.bottom - bounds.bottom);

  return offset(rect, [rightShift - leftShift, downShift - upShift]);
};

export const inflate = (rect: Rect, margin: number) =>
  new Rect(
    rect.left - margin,
    rect.top - margin,
    rect.width + 2 * margin,
    rect.height + 2 * margin
  );
